import { useCallback, useContext } from 'react';

import type {
	ClearReferrerCallback,
	RefreshSearchSessionIdCallback,
	SetQueryParamsCallback,
	SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import type { SearchQueryStringParams } from '../../../../../common/constants/schemas/query-params';
import { areAllEqualQueryParams, toQueryParamsRaw } from '../../../../../common/utils/query-params';
import { StaticBootstrapStoreContext } from '../index';
import { useStaticBootstrapSelector } from '../static-bootstrap-store';

export const useSetQueryParamsContext = (
	queryParams: SearchQueryStringParams,
): SetQueryParamsCallback => {
	const hostProductSetQueryParamsCallback = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.queryParamsCallback,
	);

	const setQueryParamsCallback: SetQueryParamsCallback = useCallback(
		(newParams) => {
			const areEqualQueryParams = areAllEqualQueryParams(newParams, queryParams ?? {});
			if (areEqualQueryParams) {
				return;
			}
			const rawQueryParams = toQueryParamsRaw(newParams);
			hostProductSetQueryParamsCallback(rawQueryParams);
		},
		[queryParams, hostProductSetQueryParamsCallback],
	);

	return setQueryParamsCallback;
};

/**
 * Refresh the search session ID by calling the host product callback, modifies the store.
 */
export const useRefreshSearchSessionIdContext = (): RefreshSearchSessionIdCallback => {
	const refreshSearchSessionId = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.refreshSearchSessionId,
	);
	return refreshSearchSessionId;
};

/**
 * Clear the referrer by calling the host product callback, modifies the store.
 */
export const useClearReferrerContext = (): ClearReferrerCallback => {
	const clearReferrer = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.clearReferrer,
	);
	return clearReferrer;
};

/**
 * Set third party configs by calling the host product callback, modifies the store.
 */
export const useSetThirdPartyConfigsContext = (): SetThirdPartyConfigsCallback => {
	const setThirdPartyConfigs = useStaticBootstrapSelector(
		useContext(StaticBootstrapStoreContext),
		(ctx) => ctx.setThirdPartyConfigs,
	);
	return setThirdPartyConfigs;
};
